class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}


// Script below to add RLT Home Package Protection to cart
// Check cart contents and add product
function checkAndAddRLTHomePackageProtection() {
  // Get cart data via Ajax API
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const miniprotectionVariantId = 47164491858087; // Replace with the ID of the product to be added
      const compactprotectionVariantId = 47164491890855; // Replace with the ID of the product to be added
      const maxprotectionVariantId = 47164491923623; // Replace with the ID of the product to be added
      const ultraprotectionVariantId = 47164491956391; // Replace with the ID of the product to be added
      const eliteprotectionVariantId = 47164491989159; // Replace with the ID of the product to be added

      const terms = ["TOTAL SPECTRUM ELITE", "TOTAL SPECTRUM ULTRA", "TOTAL SPECTRUM MAX", "TOTAL SPECTRUM COMPACT", "TOTAL SPECTRUM MINI"];

      let matchingTerm = null;  // Default if no match

      for (const term of terms) {
        const hasMatch = cart.items.some(item => 
          item.title.toUpperCase().includes(term.toUpperCase())
        );
        
        if (hasMatch) {
          matchingTerm = term;
          break;  // Stop at the first matching term in priority order
        }
      }

      var isProtectionAvailable = false;
      var protectionToAdd = null;
      var protectionToRemove = false;

      if (isProtectionAvailable == false) {
        isProtectionAvailable = cart.items.some(item => item.variant_id == eliteprotectionVariantId);
        if (matchingTerm != "TOTAL SPECTRUM ELITE" && isProtectionAvailable) {
          protectionToRemove = true;
        }
      }

      if (isProtectionAvailable == false) {
        isProtectionAvailable = cart.items.some(item => item.variant_id == ultraprotectionVariantId);
        if (matchingTerm != "TOTAL SPECTRUM ULTRA" && isProtectionAvailable) {
          protectionToRemove = true;
        }        
      }
      
      if (isProtectionAvailable == false) {
        isProtectionAvailable = cart.items.some(item => item.variant_id == maxprotectionVariantId);
        if (matchingTerm != "TOTAL SPECTRUM MAX" && isProtectionAvailable) {
          protectionToRemove = true;
        }
      }
      
      if (isProtectionAvailable == false) {
        isProtectionAvailable = cart.items.some(item => item.variant_id == compactprotectionVariantId);
        if (matchingTerm != "TOTAL SPECTRUM COMPACT" && isProtectionAvailable) {
          protectionToRemove = true;
        }
      }
      
      if (isProtectionAvailable == false) {
        isProtectionAvailable = cart.items.some(item => item.variant_id == miniprotectionVariantId);
        if (matchingTerm != "TOTAL SPECTRUM MINI" && isProtectionAvailable) {
          protectionToRemove = true;
        }
      }
      
      if (matchingTerm == "TOTAL SPECTRUM ELITE")  {
        protectionToAdd = eliteprotectionVariantId;        
      } else if (matchingTerm == "TOTAL SPECTRUM ULTRA") {
        protectionToAdd = ultraprotectionVariantId;
      } else if (matchingTerm == "TOTAL SPECTRUM MAX") {
        protectionToAdd = maxprotectionVariantId;
      } else if (matchingTerm == "TOTAL SPECTRUM COMPACT") {
        protectionToAdd = compactprotectionVariantId;
      } else if (matchingTerm == "TOTAL SPECTRUM MINI") {
        protectionToAdd = miniprotectionVariantId;
      }

      //console.log(matchingTerm);
      //console.log(isProtectionAvailable);
      //console.log(protectionToAdd);

      if (matchingTerm == null && isProtectionAvailable && protectionToAdd == null) {
        console.log ("Entered");
        let updates = {
          47164491858087: 0,
          47164491890855: 0,
          47164491923623: 0,
          47164491956391: 0,
          47164491989159: 0
        };
        // Remove wrong protection products from the cart
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        })
        .then(response => {
          isProtectionAvailable = false;
          // Reload the page to show the updated cart
          location.reload();
        })
        .catch(error => console.error('Error adding product:', error));
      }

      if (protectionToRemove) {
        let updates = {
          47164491858087: 0,
          47164491890855: 0,
          47164491923623: 0,
          47164491956391: 0,
          47164491989159: 0
        };
        // Remove wrong protection products from the cart
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        })
        .then(response => {
        isProtectionAvailable = false;
          // Update cart but do not reload page
          return response.json();
        })
        .catch(error => console.error('Error adding product:', error));
      }     

      if (matchingTerm != null && !isProtectionAvailable) {
        // Add the protection product to the cart
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: protectionToAdd,
              quantity: 1
            }]
          })
        })
        .then(response => {
          // Reload the page to show the updated cart
          location.reload();
        })
        .catch(error => console.error('Error adding product:', error));
      }
    });
}

// Run the function when the page loads
window.onload = checkAndAddRLTHomePackageProtection;
